import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * Cart Drawer Component
 */
class CartDrawerComponent extends DialogComponent {

  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );

    this.addEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose
    );

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }

    // Keep Shopify checkout available as the resilient, global-capable path.
    this.restoreShopifyCheckout();

    // Shopify dynamically redraws cart drawer
    this.#observer = new MutationObserver(() => {
      this.restoreShopifyCheckout();
    });

    this.#observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#updateStickyState
    );

    this.removeEventListener(
      DialogOpenEvent.eventName,
      this.#handleHistoryOpen
    );

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleHistoryClose
    );

    this.#historyAbortController?.abort();

    this.#observer?.disconnect();
  }

  /**
   * Restore the native Shopify checkout if an older storefront script hid it.
   */
  restoreShopifyCheckout() {
    const summary = document.querySelector('.cart-drawer__summary');
    if (!summary) return;

    summary.querySelector('.custom-whatsapp-btn')?.remove();

    const checkoutBtn = summary.querySelector('button[name="checkout"]');
    checkoutBtn?.style.removeProperty('display');
  }

  #handleHistoryOpen = () => {

    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {

      history.pushState(
        { cartDrawerOpen: true },
        ''
      );
    }

    this.#historyAbortController =
      new AbortController();

    window.addEventListener(
      'popstate',
      this.#handlePopState,
      {
        signal:
          this.#historyAbortController.signal,
      }
    );
  };

  #handleHistoryClose = () => {

    this.#historyAbortController?.abort();

    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {

    if (this.refs.dialog?.open) {

      this.refs.dialog.style.setProperty(
        '--dialog-drawer-closing-animation',
        'none'
      );

      await this.closeDialog();

      this.refs.dialog.style.removeProperty(
        '--dialog-drawer-closing-animation'
      );
    }
  };

  #handleCartAdd = () => {

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {

    this.showDialog();

    customElements
      .whenDefined('shopify-payment-terms')
      .then(() => {

        const installmentsContent =
          document.querySelector(
            'shopify-payment-terms'
          )?.shadowRoot;

        const cta =
          installmentsContent?.querySelector(
            '#shopify-installments-cta'
          );

        cta?.addEventListener(
          'click',
          this.closeDialog,
          { once: true }
        );
      });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {

    const { dialog } = this.refs;

    if (!dialog) return;

    const content =
      dialog.querySelector(
        '.cart-drawer__content'
      );

    const summary =
      dialog.querySelector(
        '.cart-drawer__summary'
      );

    if (!content || !summary) {

      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}
